import { useCallback } from 'react';
import { toast } from '@/ui/toast';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { AiSelectedAnalysisEnqueueResponse } from '@/components/action-buttons/use-ai-analysis-orchestration';
import { useMarketplaceApi } from '@/context/marketplace.context';
import type { ActionButtonsActiveTable } from '@/components/action-buttons/action-buttons-view.types';

type UseAiAnalysisActionsParams = {
    activeTable: ActionButtonsActiveTable;
    accessToken: string;
    pickedReviews: string[];
    pickedProducts: string[];
    pickedBrandGroups: string[];
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
    allowAiAnalysis: boolean;
    aiAnalysisDisabledReason: string | null;
    resolveSelectedBrandProductGroupIds: (selectedBrandGroupIds: string[]) => string[];
    startAiAnalysis: (
        initialTotal: number,
        enqueueRequest: (operationId: string) => Promise<AiSelectedAnalysisEnqueueResponse>,
    ) => Promise<void>;
};

type UseAiAnalysisActionsReturn = {
    runAiAnalysisForSelectedReviews: () => Promise<void>;
    runAiAnalysisForSelectedProducts: () => Promise<void>;
};

export const useAiAnalysisActions = ({
    activeTable,
    accessToken,
    pickedReviews,
    pickedProducts,
    pickedBrandGroups,
    products,
    brandGroups,
    allowAiAnalysis,
    aiAnalysisDisabledReason,
    resolveSelectedBrandProductGroupIds,
    startAiAnalysis,
}: UseAiAnalysisActionsParams): UseAiAnalysisActionsReturn => {
    const { reviewsApi, productsApi } = useMarketplaceApi();

    const runAiAnalysisForSelectedReviews = useCallback(async () => {
        if (!allowAiAnalysis) {
            toast.warning(aiAnalysisDisabledReason ?? 'ИИ-анализ недоступен');
            return;
        }

        const pickedReviewIds = [...new Set(pickedReviews)];

        if (!pickedReviewIds.length) {
            toast.warning('Выберите отзывы');
            return;
        }

        await startAiAnalysis(pickedReviewIds.length, (operationId) =>
            reviewsApi.enqueueAiSelectedAnalysis(
                { reviewFeedItemIds: pickedReviewIds, operationId },
                { authToken: accessToken },
            ),
        );
    }, [accessToken, aiAnalysisDisabledReason, allowAiAnalysis, pickedReviews, startAiAnalysis]);

    const runAiAnalysisForSelectedProducts = useCallback(async () => {
        if (!allowAiAnalysis) {
            toast.warning(aiAnalysisDisabledReason ?? 'ИИ-анализ недоступен');
            return;
        }

        const pickedProductIds =
            activeTable === 'brands'
                ? resolveSelectedBrandProductGroupIds([...new Set(pickedBrandGroups)])
                : [...new Set(pickedProducts)];

        if (!pickedProductIds.length) {
            toast.warning(activeTable === 'brands' ? 'Выберите бренды' : 'Выберите продукты');
            return;
        }

        const estimatedReviewsWithoutAi =
            activeTable === 'brands'
                ? (() => {
                      const pickedBrandGroupIdSet = new Set(pickedBrandGroups);
                      return brandGroups.reduce((acc, brandGroup) => {
                          if (!pickedBrandGroupIdSet.has(brandGroup.id)) {
                              return acc;
                          }

                          const reviewCount = brandGroup.reviewCount ?? 0;
                          const aiAnalysisCount = brandGroup.aiAnalysisCount ?? 0;
                          return acc + Math.max(0, reviewCount - aiAnalysisCount);
                      }, 0);
                  })()
                : (() => {
                      const pickedProductIdSet = new Set(pickedProductIds);
                      return products.reduce((acc, product) => {
                          if (!pickedProductIdSet.has(product.id)) {
                              return acc;
                          }

                          const reviewCount = product.reviewCount ?? 0;
                          const aiAnalysisCount = product.aiAnalysisCount ?? 0;
                          return acc + Math.max(0, reviewCount - aiAnalysisCount);
                      }, 0);
                  })();

        if (estimatedReviewsWithoutAi === 0) {
            toast.warning(
                activeTable === 'brands'
                    ? 'У выбранных брендов нет отзывов без ИИ-анализа'
                    : 'У выбранных продуктов нет отзывов без ИИ-анализа',
            );
            return;
        }

        await startAiAnalysis(estimatedReviewsWithoutAi, (operationId) =>
            productsApi.enqueueAiSelectedProductsAnalysis(
                {
                    productIds: pickedProductIds,
                    operationId,
                },
                { authToken: accessToken },
            ),
        );
    }, [
        accessToken,
        activeTable,
        aiAnalysisDisabledReason,
        allowAiAnalysis,
        brandGroups,
        pickedBrandGroups,
        pickedProducts,
        products,
        resolveSelectedBrandProductGroupIds,
        startAiAnalysis,
    ]);

    return {
        runAiAnalysisForSelectedReviews,
        runAiAnalysisForSelectedProducts,
    };
};
